/**
 * Card payment against a PRE-CREATED order: the client half of the two-step
 * flow the server enforces.
 *
 * The server side is one mechanism for every surface: `create_pending_order`,
 * then either `createSetupIntent(order_id)` (a BATCHED order -- the batch
 * release charges the saved card) or `createPaymentIntent(order_id)` (charged
 * now, at the amount the SERVER recomputes from the order), and finally
 * `checkout(order_id, setup_intent_id | payment_intent_id)`, whose finalizer
 * refuses any proof the server did not record for that order.
 *
 * These functions are the card steps of that flow for admin "Add Order".
 * They mirror CartCheckout.vue's `saveCardForBatch` / `tokenizeCard` /
 * `payNow`, which still carry their own copy (the storefront was not
 * refactored onto this module while the AdventHealth store is live).
 *
 * THE TWO STRIPE INSTANCES. The card Elements belong to the PLATFORM instance
 * (`setupStripe`), because `confirmCardSetup` has no per-call account override
 * and the SetupIntent lives on the platform. A PaymentIntent lives on the
 * CONNECTED account (`stripe`), so the immediate-charge path carries the card
 * there with a single-use token -- a token crosses accounts; a platform-owned
 * PaymentMethod does not (verified against the live API, see CartCheckout).
 *
 * Every function RESOLVES -- `{ ...result }` or `{ error }` -- and never
 * dispatches: the caller decides how to tell the user. A returned `error` is
 * a user-presentable message or an axios error for `raiseError`.
 */

/**
 * Save the card against a batched order. Resolves `{ setupIntent }` (status
 * `succeeded`) or `{ error }`.
 */
export async function saveCardForOrder({ axios, setupStripe, cardElement, orderId, name }) {
  let result;
  try {
    result = await axios.post("/stripe/createSetupIntent", { order_id: orderId });
  } catch (err) {
    return { error: err };
  }
  if (result.data && result.data.error) {
    return { error: result.data.error };
  }
  const clientSecret = result.data && result.data.client_secret;
  if (!clientSecret) {
    // The server refused to link a SetupIntent to the order; confirming
    // anything now would vault a card the finalizer can never match.
    return { error: "The card could not be saved." };
  }

  const confirm = await setupStripe.confirmCardSetup(clientSecret, {
    payment_method: { card: cardElement, billing_details: { name } },
  });
  if (confirm.error) {
    return { error: confirm.error.message };
  }
  const setupIntent = confirm.setupIntent;
  if (!setupIntent || setupIntent.status !== "succeeded") {
    return { error: "Sorry, please try again with a different payment method." };
  }
  return { setupIntent };
}

/**
 * Charge the card now, against an unbatched order. The amount is the
 * server's: `createPaymentIntent` recomputes it from the order and ignores
 * the client figure. Resolves `{ paymentIntent }` (status `succeeded`) or
 * `{ error }`.
 */
export async function chargeOrder({
  axios,
  stripe,
  setupStripe,
  cardElement,
  orderId,
  name,
  description,
  taxCollectedCents,
}) {
  let result;
  try {
    result = await axios.post("/stripe/createPaymentIntent", {
      payment_method_types: "card",
      currency: "usd",
      description,
      // PaymentIntent metadata only; the amount itself is the server's.
      tax_colleted: taxCollectedCents,
      order_id: orderId,
    });
  } catch (err) {
    return { error: err };
  }
  if (result.data && result.data.error) {
    return { error: result.data.error };
  }
  const clientSecret = result.data && result.data.client_secret;
  if (!clientSecret) {
    return { error: "The payment could not be started." };
  }

  let token;
  try {
    token = await setupStripe.createToken(cardElement, { name });
  } catch (err) {
    return { error: err };
  }
  if (!token || token.error || !token.token) {
    return {
      error: (token && token.error && token.error.message) || "The card could not be read.",
    };
  }

  const confirm = await stripe.confirmCardPayment(clientSecret, {
    payment_method: { card: { token: token.token.id }, billing_details: { name } },
  });
  if (confirm.error) {
    return { error: confirm.error.message };
  }
  const paymentIntent = confirm.paymentIntent;
  if (!paymentIntent || paymentIntent.status !== "succeeded") {
    return { error: "Sorry, please try again with a different payment method." };
  }
  return { paymentIntent };
}
